/**
 * HorrorCharacterIcon — SVG glyph icon for each Horror Race archetype.
 *
 * Ported faithfully from docs/wireframes/horror-race/_horror-icons.js.
 * Each icon shares the same 24×24 viewBox with a circular frame (r=8.3)
 * and an inner glyph that distinguishes the archetype. Monochrome:
 * inherits `currentColor` so the parent can drive color via CSS or a prop.
 *
 * Props:
 *   character — one of the 5 engine Character values (VAMPIRE etc.)
 *   size      — pixel side-length of the square SVG (default 24)
 *   color     — optional explicit color override (defaults to currentColor)
 */

import type { Character } from "@cloudnest/horror-race-engine";

const S =
  'stroke="currentColor" fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"';
const F = 'fill="currentColor" stroke="none"';
const ring = `<circle ${S} cx="12" cy="12" r="8.3"/>`;

/** Raw SVG markup (sans outer <svg>) for each archetype. */
const ICON_INNER: Record<Character, string> = {
  // 뱀파이어 — eyes + fanged mouth
  VAMPIRE:
    ring +
    `<circle ${F} cx="9.4" cy="10.4" r="0.9"/>` +
    `<circle ${F} cx="14.6" cy="10.4" r="0.9"/>` +
    `<path ${S} stroke-width="1.3" d="M8.4 13 Q12 15.3 15.6 13"/>` +
    `<path ${F} d="M9.9 13.3 L10.7 15.9 L11.5 13.3 Z"/>` +
    `<path ${F} d="M12.5 13.3 L13.3 15.9 L14.1 13.3 Z"/>`,

  // 강시 — forehead talisman + eyes
  JIANGSHI:
    ring +
    `<rect ${S} stroke-width="1.3" x="10.4" y="4.8" width="3.2" height="4.6" rx="0.6"/>` +
    `<path ${S} stroke-width="1.05" d="M11.1 6.3 H12.9 M11.1 7.7 H12.9"/>` +
    `<circle ${F} cx="9.6" cy="13.4" r="0.9"/>` +
    `<circle ${F} cx="14.4" cy="13.4" r="0.9"/>`,

  // 구미호 — fox ears (inside circle) + eyes + nose
  GUMIHO:
    ring +
    `<path ${S} stroke-width="1.4" d="M8.6 8.6 L9.9 6.3 L11.2 8.6"/>` +
    `<path ${S} stroke-width="1.4" d="M12.8 8.6 L14.1 6.3 L15.4 8.6"/>` +
    `<circle ${F} cx="9.8" cy="12.2" r="0.85"/>` +
    `<circle ${F} cx="14.2" cy="12.2" r="0.85"/>` +
    `<path ${S} stroke-width="1.3" d="M12 13.5 V14.9 M10.8 16 Q12 16.8 13.2 16"/>`,

  // 미이라 — bandage wrapping + eye slit
  MUMMY:
    ring +
    `<path ${S} stroke-width="1.3" d="M6 9.6 L18 7.2 M5.5 13 L18.5 10.6 M6.8 16.4 L17.2 15"/>` +
    `<path ${S} stroke-width="2.1" d="M9.6 11.7 L13 11.2"/>`,

  // 좀비 — X eyes + stitched mouth
  ZOMBIE:
    ring +
    `<path ${S} stroke-width="1.4" d="M8 9 l2.2 2.2 M10.2 9 l-2.2 2.2 M13.8 9 l2.2 2.2 M16 9 l-2.2 2.2"/>` +
    `<path ${S} stroke-width="1.4" d="M8 16 H16 M9.5 14.8 V17.2 M12 14.8 V17.2 M14.5 14.8 V17.2"/>`,
};

const CHARACTER_LABELS: Record<Character, string> = {
  VAMPIRE: "뱀파이어",
  JIANGSHI: "강시",
  GUMIHO: "구미호",
  MUMMY: "미이라",
  ZOMBIE: "좀비",
};

interface HorrorCharacterIconProps {
  readonly character: Character;
  readonly size?: number;
  readonly color?: string;
}

export function HorrorCharacterIcon({
  character,
  size = 24,
  color,
}: HorrorCharacterIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      data-testid="horror-char-icon"
      data-hr-char={character}
      aria-label={CHARACTER_LABELS[character]}
      role="img"
      style={{
        display: "block",
        color: color ?? "currentColor",
        flexShrink: 0,
      }}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted static SVG paths ported from wireframe
      dangerouslySetInnerHTML={{ __html: ICON_INNER[character] }}
    />
  );
}
